import { Card, SxProps, Theme } from "@mui/material";
//import { Annotation } from "components/PDFCanvas3";
import useIsInViewport from "helpers/hooks/useIsInViewport";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Page } from "react-pdf";
import { PageCallback } from "react-pdf/dist/cjs/shared/types.d.ts";
import theme from "theme";
//import theme from "theme";
import { P } from "theme/typography";
import { Annotation } from "components/AnnotationInterface";

interface DocumentViewerPageProps {
  paginatorPageNumber: number;
  segmentPageNumber: number;
  intakePageNumber: number;
  scale: number;
  loadingSx: SxProps<Theme>;
  pageRotate: number;
  setPageRotate: (rotate: number) => void;
  userRotate: number;
  onHover?: (x: number, y: number) => void;
  setPageProxy: (page: PageCallback | undefined) => void;
  setVisiblePageNumber: (pageNumber: number) => void;
  annotations?: Annotation[];
  addDocumentViewerPageRef: (intakePageNumber: number, ref: HTMLCanvasElement) => void;
  removeDocumentViewerPageRef: (intakePageNumber: number) => void;
}

/**
 * Convert a size you want in pixels accounting for the screen resolution
 * @param value The size you want in pixels
 * @param dpr window.devicePixelRatio
 * @returns the value needed to match the size you want in pixels
 */
function toPx(value: number, dpr: number): number {
  return value / dpr;
}

function isPageLandscape(rotate: number): boolean {
  return rotate === 90 || rotate === 270;
}

const drawCheckMark = (body: {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  width: number;
  blockHeight: number;
  dpr: number;
}) => {
  const { ctx, x, y, dpr, blockHeight } = body;
  const checkmarkWidth = toPx(body.width, dpr);
  const checkmarkHeight = checkmarkWidth * (2 / 3);
  const x1 = x;
  const x2 = x + checkmarkWidth / 4;
  const x3 = x + checkmarkWidth;
  const y1 = y + blockHeight / 2;
  const y2 = y1 + checkmarkHeight / 2;
  const y3 = y1 - checkmarkHeight / 2;

  ctx.globalAlpha = 0.6;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.lineWidth = 1;
  ctx.strokeStyle = theme.palette.success.light;
  ctx.stroke();
};

const DocumentViewerPage: FC<DocumentViewerPageProps> = ({
  paginatorPageNumber,
  segmentPageNumber,
  intakePageNumber,
  setPageProxy,
  setVisiblePageNumber,
  annotations,
  onHover,
  scale,
  userRotate,
  pageRotate,
  loadingSx,
  setPageRotate,
  addDocumentViewerPageRef,
  removeDocumentViewerPageRef,
}) => {
  const [canvas, setCanvasRef] = useState<HTMLCanvasElement | undefined>(undefined);
  const [pageCanvasRef, setPageCanvasRef] = useState<HTMLCanvasElement | undefined>(undefined);
  const canvasRefSet = useCallback((ref: HTMLCanvasElement) => {
    setCanvasRef(ref);
  }, []);
  const pageCanvasRefSet = useCallback((ref: HTMLCanvasElement) => setPageCanvasRef(ref), []);
  const dpr = window.devicePixelRatio || 1;
  const isInViewport = useIsInViewport(pageCanvasRef, 0.6);
  const [internalPageProxy, setInternalPageProxy] = useState<PageCallback | undefined>(undefined);
  const [internalPageRotate, setInternalPageRotate] = useState<number>(0);
  useEffect(() => {
    if (isInViewport && internalPageProxy) {
      setPageProxy(internalPageProxy);
      setPageRotate(internalPageRotate);
      setVisiblePageNumber(intakePageNumber);
    }
  }, [isInViewport, internalPageProxy, internalPageRotate]);

  useEffect(() => {
    return () => removeDocumentViewerPageRef(paginatorPageNumber);
  }, []);

  useEffect(() => {
    if (pageCanvasRef !== undefined) {
      addDocumentViewerPageRef(intakePageNumber, pageCanvasRef);
    }
  }, [intakePageNumber, pageCanvasRef]);

  // Sizing effect
  useEffect(() => {
    if (!canvas || !internalPageProxy || !pageCanvasRef) return;
    const landscape = isPageLandscape(userRotate);
    const pageWidth = (landscape ? pageCanvasRef.height : pageCanvasRef.width) / dpr;
    const pageHeight = (landscape ? pageCanvasRef.width : pageCanvasRef.height) / dpr;

    canvas.width = pageWidth;
    canvas.height = pageHeight;
    canvas.style.width = pageWidth + "px";
    canvas.style.height = pageHeight + "px";

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
  }, [canvas, internalPageProxy, scale, dpr, pageCanvasRef, userRotate]);

  // Drawing effect
  useEffect(() => {
    if (!canvas || !internalPageProxy || !pageCanvasRef) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const scaledHeight = canvas.height / dpr;
    const scaledWidth = canvas.width / dpr;
    ctx.clearRect(0, 0, scaledWidth, scaledHeight);

    if (annotations) {
      annotations.forEach((ann) => {
        // Turn transparency on
        ctx.globalAlpha = 0.4;
        const top = Math.round(ann.top * scaledHeight);
        const left = Math.round(ann.left * scaledWidth);
        const width = Math.round(ann.width * scaledWidth);
        const height = Math.round(ann.height * scaledHeight);
        ctx.fillStyle = ann.color;
        ctx.fillRect(left, top, width, height);

        if (ann.pinned) {
          drawCheckMark({
            ctx,
            x: left + width + toPx(4, dpr),
            y: top,
            width: 15,
            blockHeight: height,
            dpr,
          });
        }

        // const middle = Math.floor(top + height / 2 - iconSize / 2);
        // if (ann.type) {
        //   const img = makeIconImage(ann.type);
        //   drawImage(ctx, img, 15, middle, iconSize, iconSize);
        // }
      });
    }
  }, [canvas, annotations, scale, dpr, pageCanvasRef, internalPageProxy]);

  const translateMouseEventCoords = (evt: React.MouseEvent) => {
    if (!canvas) return undefined;
    const rect = canvas.getBoundingClientRect();
    const x = (evt.clientX - rect.left) / rect.width;
    const y = (evt.clientY - rect.top) / rect.height;
    switch (userRotate) {
      case 0:
        return [x, y];
      case 90:
        return [y, 1 - x];
      case 180:
        return [1 - x, 1 - y];
      case 270:
        return [1 - y, x];
      default:
        return [x, y];
    }
  };

  const handleHover: React.MouseEventHandler = (evt) => {
    const coords = translateMouseEventCoords(evt);
    if (coords) {
      const [x, y] = coords;
      if (onHover && x !== undefined && y !== undefined) {
        onHover(x, y);
      }
    }
  };

  const calcTop = useMemo(() => {
    switch (userRotate) {
      case 0:
        return 0;
      case 90:
        return 0;
      case 180:
        return pageCanvasRef ? pageCanvasRef.height / dpr : 0;
      case 270:
        return pageCanvasRef ? pageCanvasRef.height / dpr : 0;
      default:
        return 0;
    }
  }, [userRotate, pageCanvasRef?.width, pageCanvasRef?.height, dpr]);
  const calcLeft = useMemo(() => {
    switch (userRotate) {
      case 0:
        return 0;
      case 90:
        return pageCanvasRef ? pageCanvasRef.width / dpr : 0;
      case 180:
        return pageCanvasRef ? pageCanvasRef.width / dpr : 0;
      case 270:
        return 0;
      default:
        return 0;
    }
  }, [userRotate, pageCanvasRef?.width, pageCanvasRef?.height, dpr]);

  return (
    <Page
      canvasRef={pageCanvasRefSet}
      pageNumber={segmentPageNumber}
      scale={scale}
      rotate={(pageRotate + userRotate) % 360}
      renderTextLayer={false}
      renderAnnotationLayer={false}
      loading={
        <Card sx={{ ...loadingSx, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <P>Page Loading</P>
        </Card>
      }
      onLoadSuccess={(page: PageCallback) => {
        const rotate = (page as PageCallback & { rotate: number }).rotate;
        setInternalPageRotate(rotate);
        setInternalPageProxy(page);
      }}>
      {pageCanvasRef && (
        <canvas
          onMouseMove={handleHover}
          style={{
            position: "absolute",
            transform: `rotate(${userRotate}deg)`,
            transformOrigin: "top left",
            top: calcTop,
            left: calcLeft,
          }}
          // hidden={!props.annotate}
          ref={canvasRefSet}
        />
      )}
    </Page>
  );
};

export default DocumentViewerPage;
